import React, { useEffect, useState, useCallback } from 'react'
import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import update from 'immutability-helper'
import arraySort from 'array-sort'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import colors from '../../../configuration/colors'
import Button from '../../mini/Buttons/Button'
import CourseHeader from './CourseHeader'
import { showModal } from '../../../actions/modal'
import JourneyRow from '../Journey/JourneyRow'
import { fetchActivityCourseJourneys } from '../API'
import withApiData from '../../HOC/withApiData'
import { useIsCourseExpanded } from '../hooks/useIsExpanded'
import { setCourseIsExpanded } from '../../../actions/mycourses'

const CourseContainer = styled.li`
  height: auto;
  border-radius: 4px;
  background-color: ${colors.white};
  box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.12);
  margin: 20px 0;
  padding: 10px 0 10px 20px;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`

export const Course = ({
  apiData,
  activityCourse,
  academicYears,
  selectedAcademicYearId,
  gradeLevels,
  orgFlags
}) => {
  const dispatch = useDispatch()

  const canCollapse = activityCourse.courses_count > 0
  const isExpanded = useIsCourseExpanded(activityCourse)
  const shouldShowJourneys = isExpanded || !canCollapse

  const { data: journeys, refetch: fetchJourneys } = useQuery(
    ['activity-course-journeys-' + activityCourse.id],
    () =>
      fetchActivityCourseJourneys(
        apiData.orgId,
        apiData.token,
        activityCourse.id
      ),
    {
      refetchOnWindowFocus: false,
      enabled: shouldShowJourneys
    }
  )

  const [journeysState, setJourneysState] = useState(null)

  useEffect(() => {
    if (journeysState !== journeys) {
      setJourneysState(arraySort(journeys, 'master_course_course.sort_order'))
    }
  }, [journeys])

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const journeysCopy = [...journeysState]
      const dragCard = journeysCopy[dragIndex]
      setJourneysState(
        update(journeysCopy, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard]
          ]
        })
      )
    },
    [journeysState]
  )

  const renderCard = (journey, index) => {
    return (
      <JourneyRow
        activityCourse={activityCourse}
        key={index}
        index={index}
        journey={journey}
        moveCard={moveCard}
        id={journey.id}
        selectedAcademicYearId={selectedAcademicYearId}
      />
    )
  }

  const handleDisplayJourneysList = () => {
    const isShown = shouldShowJourneys
    if (!isShown) {
      fetchJourneys()
    }
    dispatch(setCourseIsExpanded(activityCourse.id, !isShown))
  }

  const handleDisplayJourneysListOnCreation = () => {
    const isShown = shouldShowJourneys
    if (!isShown) {
      fetchJourneys()
    }
    dispatch(setCourseIsExpanded(activityCourse.id, true))
  }
  const handleJourneyCreationModal = () => {
    dispatch(
      showModal('STARTER_JOURNEY_MODAL', {
        gradeLevels: gradeLevels,
        activityCourse: activityCourse,
        academicYears: academicYears,
        selectedAcademicYearId,
        handleDisplayJourneysListOnCreation,
        orgFlags
      })
    )
  }

  return (
    <CourseContainer
      onClick={e =>
        typeof e.target.className === 'string' &&
        (e.target.className.includes('CourseHeader') ||
          e.target.className.includes('Course__CourseContainer') ||
          e.target.className.includes('H5')) &&
        handleDisplayJourneysList(e)
      }
      clickable={canCollapse}
      className="activity-course"
    >
      <CourseHeader
        activityCourse={activityCourse}
        academicYears={academicYears}
        selectedAcademicYearId={selectedAcademicYearId}
      />
      <DndProvider backend={HTML5Backend}>
        {shouldShowJourneys && (
          <div>
            {journeysState !== null && (
              <div>
                {journeysState.map((journey, i) => renderCard(journey, i))}
              </div>
            )}
            <div style={{ padding: '10px 0' }}>
              <Button
                handleClick={handleJourneyCreationModal}
                width="136px"
                title="+ Add Journey"
                buttonColor={colors.white}
                borderColor={colors.aluminum}
                hoverBackground={colors.charcoal4}
                hoverColor={colors.white}
              />
            </div>
          </div>
        )}
      </DndProvider>
    </CourseContainer>
  )
}

Course.propTypes = {
  activityCourse: PropTypes.object.isRequired,
  academicYears: PropTypes.array.isRequired,
  selectedAcademicYearId: PropTypes.number.isRequired,
  justAddedCourse: PropTypes.object
}

export default withApiData(Course)



// WEBPACK FOOTER //
// ./src/components/mycourses/Course/Course.js